import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from 'react-query';
import DashboardLayout from './components/DashboardLayout';
import Dashboard from './pages/Dashboard';
import ServiceRegistry from './pages/ServiceRegistry';
import ServiceDiscovery from './pages/ServiceDiscovery';
import Services from './pages/Services';
import AgentPermissions from './pages/AgentPermissions';
import Settings from './pages/Settings';
import BackgroundSearch from './pages/BackgroundSearch';
import UserManagement from './pages/UserManagement';
import FeatureFlags from './pages/FeatureFlags';
import Staff from './pages/Staff';
import AuditLog from './pages/AuditLog';
import MatchReviewQueue from './pages/MatchReviewQueue';
import GenericTerms from './pages/GenericTerms';
import OnboardingSurvey from './pages/OnboardingSurvey';
import MyPermissions from './pages/MyPermissions';
import MediaLibrary from './pages/MediaLibrary';
import PipelineManagement from './pages/PipelineManagement';
import CicdSetup from './pages/CicdSetup';
import ContentDashboard from './pages/ContentDashboard';
import ContentDetail from './pages/ContentDetail';
import ContentRoutes from './pages/content/ContentRoutes';
import GuidesDashboard from './pages/GuidesDashboard';
import GuideDetail from './pages/GuideDetail';
import GuideEditor from './pages/GuideEditor';
import ConfirmChange from './pages/ConfirmChange';
import Rollback from './pages/Rollback';
import SignIn from './pages/SignIn';
import { GoogleAuthProvider, useAuth } from './contexts/GoogleAuthContext';
import { CircularProgress, Box } from '@mui/material';
import InactivityWarning from './components/InactivityWarning';
import { muiThemePartial } from './theme-tokens';

// Shared design tokens from design-tokens/tokens.json (Curated-By-Blog repo)
// To update: edit tokens.json → run `node design-tokens/build.js` → copy dist/mui-theme.ts here
const theme = createTheme(muiThemePartial);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

function ProtectedRoutes() {
  const { user, loading, showTimeoutWarning, stayLoggedIn, signOut } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <SignIn />;
  }

  return (
    <>
      <DashboardLayout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/services" element={<Services />} />
          <Route path="/permissions" element={<AgentPermissions />} />
          <Route path="/service-registry" element={<ServiceRegistry />} />
          <Route path="/service-discovery" element={<ServiceDiscovery />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/feature-flags" element={<FeatureFlags />} />
          <Route path="/staff" element={<Staff />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/match-review" element={<MatchReviewQueue />} />
          <Route path="/generic-terms" element={<GenericTerms />} />
          <Route path="/onboarding-survey" element={<OnboardingSurvey />} />
          <Route path="/my-permissions" element={<MyPermissions />} />
          <Route path="/media-library" element={<MediaLibrary />} />
          <Route path="/pipeline" element={<PipelineManagement />} />
          <Route path="/background-search" element={<BackgroundSearch />} />
          <Route path="/cicd-setup" element={<CicdSetup />} />
          <Route path="/content" element={<ContentDashboard />} />
          <Route path="/content/:contentType/:slug" element={<ContentDetail />} />
          <Route path="/content/cms/*" element={<ContentRoutes />} />
          <Route path="/guides" element={<GuidesDashboard />} />
          <Route path="/guides/:slug" element={<GuideDetail />} />
          <Route path="/guides/:slug/edit" element={<GuideEditor />} />
          {/* ConciergeList/CreateConcierge/ConciergeDetail routes removed — pages never created */}
        </Routes>
      </DashboardLayout>
      <InactivityWarning
        open={showTimeoutWarning}
        onStayLoggedIn={stayLoggedIn}
        onLogout={signOut}
      />
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <GoogleAuthProvider>
          <Router>
            <Routes>
              <Route path="/confirm/:token" element={<ConfirmChange />} />
              <Route path="/rollback/:token" element={<Rollback />} />
              <Route path="/*" element={<ProtectedRoutes />} />
            </Routes>
          </Router>
        </GoogleAuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
